import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { filter, find, findIndex, get, isEmpty } from 'lodash';
import { Form, Row, Col, Table } from 'react-bootstrap';
import { withTracker } from 'meteor/react-meteor-data';
import { Meteor } from 'meteor/meteor';

import * as videoOps from '../../../api/Videos/video-operations';
import Videos from '../../../api/Videos/Videos';

import getDefaultValuesForForms from '../../../modules/get-default-values-for-forms';

import { ATLAS_ELEMENTS } from '../AtlasAtoms/atlas-elements';
import Loading from '../../components/Loading';
import SearchInput from '../../components/SearchInput';
import TableContent from '../../components/TableContent';
import TablePagination from '../../components/TablePagination';

const ANNOTATIONS_ATLAS = new RegExp('annotations', 'i');
const VIDEO_PROP = new RegExp('video', 'i');
const ITEMS_PER_PAGE = 5;

const VideoFormElement = ({ atlas, isAnnotationsAtlas, loading, selectedStructure, videos }) => {
  if (!isAnnotationsAtlas) return '';
  if (loading) return <Loading />;

  const elementIndex = findIndex(selectedStructure, element => ANNOTATIONS_ATLAS.test(element));
  const elementValue = selectedStructure[elementIndex];

  const { props } = find(ATLAS_ELEMENTS.properties, ['value', elementValue]) || {};
  const defaultValues = getDefaultValuesForForms({ atlas, element: elementValue, elementIndex });

  const propIndex = findIndex(props, ({ name }) => VIDEO_PROP.test(name));
  const propName = get(props, [propIndex, 'name'], '');
  const propHumanValue = get(props, [propIndex, 'humanValue'], '');

  const formElementName = `${elementValue}-${propIndex}-prop-${propName}`;

  const { defaultValue: propValue } = find(defaultValues, ['formName', formElementName]) || {};

  const [videoId, setVideoId] = useState(propValue);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, changePage] = useState(1);

  const itemsPerPage = isEmpty(videoId) ? ITEMS_PER_PAGE : ITEMS_PER_PAGE - 1;
  const skip = itemsPerPage * (currentPage - 1);
  const searchRegex = new RegExp(searchValue, 'i');

  const selectedVideo = find(videos, ['_id', videoId]);

  const nonSelectedVideos = filter(videos, video => videoOps.getId(video) !== videoId);

  const filteredVideos = filter(nonSelectedVideos, video =>
    searchRegex.test(videoOps.getName(video))
  );

  const videosToShow = filter(
    filteredVideos,
    (video, index) => index >= skip && index < skip + itemsPerPage
  );

  const handleSearch = event => {
    event.persist();
    setSearchValue(event.target.value.trim());
  };

  const listProps = {
    items: isEmpty(videoId) ? videosToShow : [selectedVideo, ...videosToShow],
    loading: false,
    deleteButton: false,
    datesColumns: false,
    iconColumn: false,
    handleSelectRow: setVideoId,
    itemIdentifier: 'id',
    selectedRow: videoId,
    clickAction: 'select'
  };

  return (
    <>
      <Table responsive hover size="lg">
        <thead>
          <tr>
            <th style={{ cursor: 'pointer' }}>
              <Form.Group as={Row} className="m-0">
                <Form.Label column sm="2">
                  {propHumanValue}
                </Form.Label>
                <Col sm="10">
                  <SearchInput onKeyUp={handleSearch} />
                </Col>
              </Form.Group>
            </th>
          </tr>
        </thead>
        <TableContent
          isTableEmpty={isEmpty(videos)}
          itemName="videos"
          numberOfColumns={2}
          {...listProps}
        />
      </Table>
      <TablePagination
        currentPage={currentPage}
        isPaginationOn
        itemsPerPage={itemsPerPage}
        totalCount={filteredVideos.length}
        changePage={changePage}
      />
      <Form.Control name={formElementName} value={videoId} className="invisible" readOnly />
    </>
  );
};

VideoFormElement.defaultProps = {
  videos: []
};

VideoFormElement.propTypes = {
  atlas: PropTypes.object.isRequired,
  isAnnotationsAtlas: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  selectedStructure: PropTypes.array.isRequired,
  videos: PropTypes.array
};

export default withTracker(({ isAnnotationsAtlas }) => {
  let videosSub = {};
  if (isAnnotationsAtlas) videosSub = Meteor.isClient && Meteor.subscribe('videos.atlas');
  return {
    loading: isAnnotationsAtlas && !videosSub.ready(),
    videos: Videos.find({}, { sort: { name: 1 } }).fetch()
  };
})(VideoFormElement);
